/**
 * BaseTool - Abstract base class for all tools
 *
 * Provides common functionality for tool lifecycle management:
 * - Enable/disable with event dispatching
 * - Double-enable/disable protection
 * - Hooks for subclass customization
 * - Debug logging
 */

import * as THREE from 'three';
import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { ITool, IPersona, IViewConfig, ToolEvents } from './types';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from './ToolSettings';
import { ToolSettingsPanel } from '../ui/ToolSettingsPanel';
import { ToolUndoRedoManager } from './tools/ToolUndoRedoManager';

/**
 * Data passed to drag event handlers
 */
export interface DragEventData {
  /** Mouse button that initiated the drag (0=left, 1=middle, 2=right) */
  button: number;
  /** Start position in screen coordinates */
  startScreen: THREE.Vector2;
  /** Current position in screen coordinates */
  currentScreen: THREE.Vector2;
  /** Start position in world coordinates (may be null if no intersection) */
  startWorld: THREE.Vector3 | null;
  /** Current position in world coordinates (may be null if no intersection) */
  currentWorld: THREE.Vector3 | null;
  /** Original mouse event */
  event: MouseEvent;
}

/**
 * Abstract base class for tools
 *
 * Provides automatic event listener management - simply implement the event handler
 * methods (onMouseDown, onMouseMove, etc.) and BaseTool will automatically:
 * - Bind them in the constructor
 * - Register them as DOM event listeners when the tool is enabled
 * - Unregister them when the tool is disabled
 *
 * Subclasses must:
 * 1. Implement `name` readonly property
 * 2. Override `onEnable()` and `onDisable()` hooks (optional)
 * 3. Optionally override `getViewConfig()` for custom views
 * 4. Optionally implement input handlers (onMouseDown, onMouseMove, etc.)
 *
 * @example
 * export class MeasurePointTool extends BaseTool {
 *   readonly name = 'measure_point';
 *
 *   protected onEnable(): void {
 *     this.engine.init();
 *   }
 *
 *   protected onDisable(): void {
 *     this.engine.dispose();
 *   }
 *
 *   // Event listeners are automatically registered/unregistered!
 *   public onMouseDown(event: MouseEvent): void {
 *     // Handle mouse down
 *   }
 * }
 */

/**
 * True when a key event originates from a text-entry element (input,
 * textarea, select, or anything contentEditable). Such keys belong to the
 * field — typing, caret movement, text undo — and must never reach tool
 * shortcuts (Backspace deleting the selected object mid-rename, Ctrl+Z
 * mutating the scene instead of the text, …).
 */
function isTextEntryEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export abstract class BaseTool extends EventDispatcher implements ITool {
  /** Viewer instance */
  protected viewer: Viewer;

  /** Parent persona */
  protected persona: IPersona;

  /** Internal enabled state */
  private _enabled = false;

  /** Debug mode flag */
  private debug = false;

  /** Settings panel element (if tool provides one) */
  private settingsPanel: HTMLElement | undefined;

  /** Settings panel instance for cleanup */
  private settingsPanelInstance: ToolSettingsPanel | undefined;
  /** Language the cached settings panel was rendered in. */
  private settingsPanelLanguage: string | undefined;

  /** Unique tool identifier - must be implemented by subclass */
  abstract readonly name: string;

  /** Bound event handlers for automatic cleanup */
  private boundHandlers: Map<string, EventListener> = new Map();

  /** Bound wheel handler for cleanup */
  private boundWheelCaptureHandler: ((e: WheelEvent) => void) | null = null;

  /** Track mouse position to detect drags */
  private mouseDownPosition: { x: number; y: number } | null = null;
  private dragThreshold = 5; // pixels - movement threshold to consider it a drag
  private rightMouseDownPosition: { x: number; y: number } | null = null; // Track right mouse button separately

  /** Undo/redo manager — initialized by tools that opt in via initUndoRedo() */
  private _undoRedo: ToolUndoRedoManager | null = null;

  /** Bound undo/redo keyboard handler for cleanup */
  private boundUndoRedoKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  /** Drag state tracking */
  protected isDragging = false;
  private dragButton = 0;
  private dragStartScreen: THREE.Vector2 | null = null;
  private dragStartWorld: THREE.Vector3 | null = null;
  private _dragStartMouseEvent: { clientX: number; clientY: number } | null = null;

  /**
   * Constructor
   *
   * @param viewer - Viewer instance
   * @param persona - Parent persona
   */
  constructor(viewer: Viewer, persona: IPersona) {
    super();
    this.viewer = viewer;
    this.persona = persona;

    // Enable debug logging via environment or localStorage
    if (typeof window !== 'undefined') {
      this.debug = localStorage.getItem('persona_debug') === 'true';
    }

    // NOTE: We don't bind event handlers in constructor anymore
    // They are bound lazily in registerEventListeners() to ensure
    // child class method overrides are properly bound
  }

  /**
   * Automatically bind event handler methods if they exist
   * This eliminates the need for manual binding in each tool
   */
  private bindEventHandlers(): void {
    const handlers = ['onMouseDown', 'onMouseUp', 'onMouseMove', 'onClick', 'onDoubleClick', 'onRightClick', 'onKeyDown', 'onKeyUp'] as const;

    for (const handlerName of handlers) {
      const method = this[handlerName];
      if (typeof method === 'function') {
        // Bind the method and store it
        let boundHandler = method.bind(this) as EventListener;
        // Keyboard handlers listen on WINDOW, so keystrokes typed into an
        // input field (rename box, property panel, search…) would also reach
        // the tool — Backspace/Delete mid-typing must edit the text, not
        // delete the selected object. Absorb any key event that originates
        // from a text-entry element before it reaches the tool.
        if (handlerName === 'onKeyDown' || handlerName === 'onKeyUp') {
          const toolHandler = boundHandler;
          boundHandler = ((event: Event) => {
            if (isTextEntryEventTarget(event.target)) return;
            toolHandler(event);
          }) as EventListener;
        }
        this.boundHandlers.set(handlerName, boundHandler);
      }
    }
  }

  /**
   * Convert screen coordinates to world coordinates using raycasting
   * Used internally for drag events
   */
  private screenToWorldInternal(_screenX: number, _screenY: number): THREE.Vector3 | null {
    // No tool currently consumes the world coordinates from DragEventData.
    // Each tool that needs world coords does its own conversion.
    // Returning null avoids expensive raycasting on every drag event.
    return null;
  }

  /**
   * Get screen position from mouse event relative to render area
   */
  private getScreenPosition(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Internal handler to track mousedown position for drag detection
   */
  private handleMouseDown = (event: MouseEvent): void => {
    this.mouseDownPosition = { x: event.clientX, y: event.clientY };

    // Track right mouse button separately for right-click detection
    if (event.button === 2) {
      this.rightMouseDownPosition = { x: event.clientX, y: event.clientY };
    }

    // Store drag start info (will be used if drag threshold is exceeded)
    this.dragButton = event.button;
    this.dragStartScreen = this.getScreenPosition(event);
    // Defer expensive world-space raycast until drag actually starts
    this.dragStartWorld = null;
    this._dragStartMouseEvent = { clientX: event.clientX, clientY: event.clientY };

    // Promote mousemove/mouseup to window so we catch releases outside the canvas.
    // Remove from domElement first to avoid double-firing.
    const domElement = this.viewer.renderer.domElement;
    domElement.removeEventListener('mousemove', this.handleMouseMove);
    domElement.removeEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);

    // Call user's onMouseDown if it exists
    const userHandler = this.boundHandlers.get('onMouseDown');
    if (userHandler) {
      userHandler(event);
    }
  };

  /**
   * Internal handler for mouse move to detect drag start and track drag
   */
  private handleMouseMove = (event: MouseEvent): void => {
    // Check if we should start dragging
    if (!this.isDragging && this.mouseDownPosition && this.dragStartScreen) {
      const dx = Math.abs(event.clientX - this.mouseDownPosition.x);
      const dy = Math.abs(event.clientY - this.mouseDownPosition.y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > this.dragThreshold) {
        // Start dragging
        this.isDragging = true;

        // Compute dragStartWorld lazily now that a drag is confirmed
        if (!this.dragStartWorld && this._dragStartMouseEvent) {
          this.dragStartWorld = this.screenToWorldInternal(this._dragStartMouseEvent.clientX, this._dragStartMouseEvent.clientY);
        }
        this._dragStartMouseEvent = null;

        // Call onDragStart if implemented
        if (this.onDragStart) {
          const currentScreen = this.getScreenPosition(event);
          const currentWorld = this.screenToWorldInternal(event.clientX, event.clientY);
          this.onDragStart({
            button: this.dragButton,
            startScreen: this.dragStartScreen,
            currentScreen,
            startWorld: this.dragStartWorld,
            currentWorld,
            event,
          });
        }
      }
    }

    // If dragging, call onDrag
    if (this.isDragging && this.dragStartScreen && this.onDrag) {
      const currentScreen = this.getScreenPosition(event);
      const currentWorld = this.screenToWorldInternal(event.clientX, event.clientY);
      this.onDrag({
        button: this.dragButton,
        startScreen: this.dragStartScreen,
        currentScreen,
        startWorld: this.dragStartWorld,
        currentWorld,
        event,
      });
    }

    // Call user's onMouseMove if it exists
    const userHandler = this.boundHandlers.get('onMouseMove');
    if (userHandler) {
      userHandler(event);
    }
  };

  /**
   * Internal handler to wrap onClick and filter out drags
   */
  private handleClick = (event: MouseEvent): void => {
    // Check if this was a drag
    if (this.mouseDownPosition) {
      const dx = Math.abs(event.clientX - this.mouseDownPosition.x);
      const dy = Math.abs(event.clientY - this.mouseDownPosition.y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > this.dragThreshold) {
        // This was a drag, don't trigger click
        this.mouseDownPosition = null;
        return;
      }
    }

    this.mouseDownPosition = null;

    // Call user's onClick handler
    const userHandler = this.boundHandlers.get('onClick');
    if (userHandler) {
      userHandler(event);
    }
  };

  /**
   * Internal handler to track mouseup for resetting drag detection
   */
  private handleMouseUp = (event: MouseEvent): void => {
    // Demote listeners back from window to domElement
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    const domElement = this.viewer.renderer.domElement;
    domElement.addEventListener('mousemove', this.handleMouseMove);
    domElement.addEventListener('mouseup', this.handleMouseUp);

    // Handle drag end
    if (this.isDragging && this.dragStartScreen) {
      if (this.onDragEnd) {
        const currentScreen = this.getScreenPosition(event);
        const currentWorld = this.screenToWorldInternal(event.clientX, event.clientY);
        this.onDragEnd({
          button: this.dragButton,
          startScreen: this.dragStartScreen,
          currentScreen,
          startWorld: this.dragStartWorld,
          currentWorld,
          event,
        });
      }
      this.isDragging = false;
    }

    // Reset drag state
    this.dragStartScreen = null;
    this.dragStartWorld = null;
    this._dragStartMouseEvent = null;

    // Handle right mouse button release (right-click)
    if (event.button === 2 && this.rightMouseDownPosition) {
      const dx = Math.abs(event.clientX - this.rightMouseDownPosition.x);
      const dy = Math.abs(event.clientY - this.rightMouseDownPosition.y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      this.rightMouseDownPosition = null;

      // Only trigger right-click if mouse didn't move much (not a drag)
      if (distance <= this.dragThreshold) {
        const rightClickHandler = this.boundHandlers.get('onRightClick');
        if (rightClickHandler) {
          rightClickHandler(event);
        }
      }
    }

    // Don't reset mouseDownPosition here - handleClick needs it for drag detection
    // handleClick will reset it after checking

    // Call user's onMouseUp if it exists
    const userHandler = this.boundHandlers.get('onMouseUp');
    if (userHandler) {
      userHandler(event);
    }
  };

  /**
   * Helper: Register DOM event listeners for this tool
   *
   * NOTE: This is called automatically by enable(). You typically don't need to call this manually.
   * Only call this if you have special requirements for manual listener management.
   */
  protected registerEventListeners(): void {
    // Bind event handlers now (not in constructor) to ensure child class
    // method overrides are properly bound
    if (this.boundHandlers.size === 0) {
      this.bindEventHandlers();
    }

    const domElement = this.viewer.renderer.domElement;

    // Mouse events on renderer - use internal handlers for drag detection
    domElement.addEventListener('mousedown', this.handleMouseDown);
    // Always use internal mouseup handler for drag detection reset
    domElement.addEventListener('mouseup', this.handleMouseUp);
    // Always use internal mousemove handler for drag detection (it calls user's onMouseMove internally)
    domElement.addEventListener('mousemove', this.handleMouseMove);

    // Use internal click handler that filters drags
    if (this.boundHandlers.has('onClick')) {
      domElement.addEventListener('click', this.handleClick);
    }
    const dblclickHandler = this.boundHandlers.get('onDoubleClick');
    if (dblclickHandler) {
      domElement.addEventListener('dblclick', dblclickHandler);
    }
    // Note: contextmenu is already handled by InputHandler

    // Undo/redo keyboard handler (registered before tool's onKeyDown to intercept first)
    if (this._undoRedo) {
      this.boundUndoRedoKeyHandler = this.handleUndoRedoKeyDown;
      window.addEventListener('keydown', this.boundUndoRedoKeyHandler);
    }

    // Keyboard events on window
    const keyDownHandler = this.boundHandlers.get('onKeyDown');
    if (keyDownHandler) {
      window.addEventListener('keydown', keyDownHandler);
    }
    const keyUpHandler = this.boundHandlers.get('onKeyUp');
    if (keyUpHandler) {
      window.addEventListener('keyup', keyUpHandler);
    }

    // Wheel: capture-phase listener on the renderer element.
    // The tool's onWheel decides whether to consume the event (via preventDefault + stopPropagation).
    // Unhandled events bubble normally to InputHandler's legacy mousewheel listener.
    if (typeof this.onWheel === 'function') {
      const boundWheel = this.onWheel.bind(this);
      this.boundWheelCaptureHandler = (e: WheelEvent) => boundWheel(e);
      domElement.addEventListener('wheel', this.boundWheelCaptureHandler, { capture: true, passive: false });
    }
  }

  /**
   * Helper: Unregister DOM event listeners for this tool
   *
   * NOTE: This is called automatically by disable(). You typically don't need to call this manually.
   * Only call this if you have special requirements for manual listener management.
   */
  protected unregisterEventListeners(): void {
    const domElement = this.viewer.renderer.domElement;

    // Remove internal mousedown, mouseup, and mousemove handlers from both
    // domElement and window (in case tool is disabled mid-drag)
    domElement.removeEventListener('mousedown', this.handleMouseDown);
    domElement.removeEventListener('mouseup', this.handleMouseUp);
    domElement.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);

    // Remove internal click handler
    if (this.boundHandlers.has('onClick')) {
      domElement.removeEventListener('click', this.handleClick);
    }
    const dblclickHandler = this.boundHandlers.get('onDoubleClick');
    if (dblclickHandler) {
      domElement.removeEventListener('dblclick', dblclickHandler);
    }
    // Note: contextmenu is handled by InputHandler, not here

    // Undo/redo keyboard handler
    if (this.boundUndoRedoKeyHandler) {
      window.removeEventListener('keydown', this.boundUndoRedoKeyHandler);
      this.boundUndoRedoKeyHandler = null;
    }

    // Keyboard events on window
    const keyDownHandler = this.boundHandlers.get('onKeyDown');
    if (keyDownHandler) {
      window.removeEventListener('keydown', keyDownHandler);
    }
    const keyUpHandler = this.boundHandlers.get('onKeyUp');
    if (keyUpHandler) {
      window.removeEventListener('keyup', keyUpHandler);
    }

    // Wheel: remove capture-phase listener
    if (this.boundWheelCaptureHandler) {
      domElement.removeEventListener('wheel', this.boundWheelCaptureHandler, { capture: true });
      this.boundWheelCaptureHandler = null;
    }

    // Clear bound handlers so they can be re-bound on next enable
    // This ensures child class overrides are picked up correctly
    this.boundHandlers.clear();
  }

  /**
   * Get current enabled state
   */
  get enabled(): boolean {
    return this._enabled;
  }

  /** Regenerate an already-created settings panel from the current schema (no-op before first use). */
  protected rebuildSettingsPanelIfCreated(): void {
    if (!this.settingsPanelInstance || typeof this.getSettingsSchema !== 'function') return;
    const schema = this.getSettingsSchema();
    if (schema) this.settingsPanelInstance.rebuild(schema);
  }

  /**
   * Get or create the settings panel instance
   * Called by React components to mount the settings panel
   */
  getSettingsPanel(): ToolSettingsPanel | undefined {
    // A cached panel built for another language keeps stale labels — drop it
    // so it re-renders through the current translation map.
    if (this.settingsPanelInstance && this.settingsPanelLanguage !== this.viewer.translationService.language) {
      this.settingsPanelInstance.destroy();
      this.settingsPanelInstance = undefined;
    }

    // Lazily create the settings panel when requested
    if (!this.settingsPanelInstance && typeof this.getSettingsSchema === 'function') {
      const schema = this.getSettingsSchema();
      if (schema) {
        this.settingsPanelLanguage = this.viewer.translationService.language;
        this.settingsPanelInstance = new ToolSettingsPanel(schema, {
          title: `${this.name} Settings`,
          position: { top: '62px', right: '10px' },
          translate: (source) => this.viewer.translationService.t(source),
          onSettingChange: (event) => {
            // Call the tool's onSettingsChange handler if it exists
            if (typeof this.onSettingsChange === 'function') {
              this.onSettingsChange(event);
            }
          },
        });
      }
    }

    return this.settingsPanelInstance;
  }

  /**
   * Enable the tool
   *
   * - Prevents double-enable
   * - Automatically registers event listeners
   * - Calls onEnable() hook
   * - Dispatches ENABLED event
   * - Logs in debug mode
   */
  enable(): void {
    if (this._enabled) {
      return;
    }

    this._enabled = true;

    try {
      // Register event listeners if tool provides handlers
      this.registerEventListeners();

      this.onEnable();
    } catch (error) {
      console.error(`[BaseTool] Error enabling tool "${this.name}":`, error);
      // Rollback state on error
      this._enabled = false;
      this.unregisterEventListeners(); // Clean up any registered listeners
      throw error;
    }

    this.dispatchEvent({
      type: ToolEvents.ENABLED,
      tool: this,
    });
  }

  /**
   * Disable the tool
   *
   * - Prevents double-disable
   * - Calls onDisable() hook
   * - Automatically unregisters event listeners
   * - Dispatches DISABLED event
   * - Logs in debug mode
   */
  disable(): void {
    if (!this._enabled) {
      return;
    }

    this._enabled = false;

    try {
      // Call subclass hook first
      this.onDisable();
    } catch (error) {
      console.error(`[BaseTool] Error disabling tool "${this.name}":`, error);
      // Don't rollback state - we want it disabled even if cleanup fails
    }

    // Clear undo/redo history (per-activation session)
    if (this._undoRedo) {
      this._undoRedo.clear();
      this.dispatchUndoRedoChanged();
    }

    // Unregister event listeners
    this.unregisterEventListeners();

    // Cleanup settings panel
    if (this.settingsPanelInstance) {
      this.settingsPanelInstance.destroy();
      this.settingsPanelInstance = undefined;
    }

    // Remove settings panel from DOM
    if (this.settingsPanel && this.settingsPanel.parentElement) {
      this.settingsPanel.parentElement.removeChild(this.settingsPanel);
    }
    this.settingsPanel = undefined;

    this.dispatchEvent({
      type: ToolEvents.DISABLED,
      tool: this,
    });

    // Also dispatch on viewer.scene so the React HintBar can clear the hint
    this.viewer.scene.dispatchEvent({
      type: ToolEvents.DISABLED,
      tool: this,
    });
  }

  /**
   * Hook: Called when tool is enabled
   * Override this in subclasses to implement tool-specific activation logic
   *
   * @example
   * protected onEnable(): void {
   *   this.engine.init();
   * }
   */
  protected onEnable(): void {
    // Subclasses should override
  }

  /**
   * Hook: Called when tool is disabled
   * Override this in subclasses to implement tool-specific deactivation logic
   *
   * @example
   * protected onDisable(): void {
   *   this.engine.dispose();
   * }
   */
  protected onDisable(): void {
    // Subclasses should override
  }

  /**
   * Get view configuration for this tool
   *
   * Override this to provide custom view modes for the tool.
   * Return undefined/empty to inherit from persona.
   *
   * @returns View configuration with available view modes
   *
   * @example
   * getViewConfig(): IViewConfig {
   *   return createMultiViewConfig([
   *     ViewModes.PERSPECTIVE_3D_RGB,
   *     ViewModes.ORTHOGRAPHIC_2D_TOP,
   *   ]);
   * }
   */
  getViewConfig?(): IViewConfig;

  /**
   * Get declarative settings schema for this tool
   *
   * Override this to provide a settings schema that will be automatically
   * rendered in the toolbar settings menu. This is the recommended approach
   * for simple settings UIs.
   *
   * @returns Settings schema object defining the tool's settings
   *
   * @example
   * getSettingsSchema?(): ToolSettingsSchema {
   *   return {
   *     sphereRadius: {
   *       type: 'slider',
   *       label: 'Sphere Radius',
   *       description: 'Size of the sphere markers',
   *       defaultValue: 0.5,
   *       min: 0.1,
   *       max: 2.0,
   *       step: 0.1,
   *       unit: 'm'
   *     },
   *     showLabels: {
   *       type: 'toggle',
   *       label: 'Show Labels',
   *       description: 'Display measurement labels',
   *       defaultValue: true
   *     }
   *   };
   * }
   */
  getSettingsSchema?(): ToolSettingsSchema;

  /**
   * Called when a setting value changes in the settings panel
   */
  onSettingsChange?(event: ToolSettingsChangeEvent): void;

  /**
   * Optional input event handlers
   * Override these in subclasses to handle user input
   */

  onMouseDown?(event: MouseEvent): void;
  onMouseMove?(event: MouseEvent): void;
  onMouseUp?(event: MouseEvent): void;
  onClick?(event: MouseEvent): void;
  onDoubleClick?(event: MouseEvent): void;
  onRightClick?(event: MouseEvent): void;
  onKeyDown?(event: KeyboardEvent): void;
  onKeyUp?(event: KeyboardEvent): void;
  /**
   * Called on wheel events via Potree's InputHandler pipeline.
   * Call event.preventDefault() if you want to consume the event.
   */
  onWheel?(event: WheelEvent): void;

  /**
   * Called when a drag operation starts (mouse moved > 5px from mousedown position)
   * Provides both screen and world coordinates for start and current positions
   */
  onDragStart?(data: DragEventData): void;

  /**
   * Called during a drag operation (on each mouse move while dragging)
   * Provides both screen and world coordinates for start and current positions
   */
  onDrag?(data: DragEventData): void;

  /**
   * Called when a drag operation ends (mouse released after dragging)
   * Provides both screen and world coordinates for start and end positions
   */
  onDragEnd?(data: DragEventData): void;

  /**
   * Cleanup and dispose resources
   *
   * - Disables tool if enabled
   * - Removes event listeners
   * - Cleans up references
   *
   * Override this if you have additional cleanup needs,
   * but make sure to call super.dispose() at the end.
   *
   * @example
   * dispose(): void {
   *   this.cleanupCustomResources();
   *   super.dispose();
   * }
   */
  dispose(): void {
    if (this._enabled) {
      this.disable();
    }

    // Remove all event listeners
    this.removeEventListeners(ToolEvents.ENABLED);
    this.removeEventListeners(ToolEvents.DISABLED);
    this.removeEventListeners(ToolEvents.STATE_CHANGED);
    this.removeEventListeners(ToolEvents.UNDO_REDO_CHANGED);
  }

  /**
   * Helper: Dispatch a state changed event
   * Use this to notify listeners of tool-specific state changes.
   *
   * Used by the quest/tooltip system: tools dispatch `{ phase: 'some_phase' }`
   * and the QuestRegistry resolves it to a translated tooltip string.
   *
   * @param state - State object with a `phase` field
   *
   * @example
   * this.dispatchStateChanged({ phase: 'idle' });
   * this.dispatchStateChanged({ phase: 'detecting' });
   */
  protected dispatchStateChanged(state: { phase: string }): void {
    this.dispatchEvent({
      type: ToolEvents.STATE_CHANGED,
      tool: this,
      state,
    });

    // Also dispatch on viewer.scene so the React HintBar can listen centrally
    this.viewer.scene.dispatchEvent({
      type: ToolEvents.STATE_CHANGED,
      tool: this,
      state,
    });
  }

  /**
   * Helper: Check if tool is enabled
   * Useful for guarding method calls
   *
   * @returns True if enabled
   *
   * @example
   * if (!this.isEnabled()) return;
   */
  protected isEnabled(): boolean {
    return this._enabled;
  }

  /**
   * Helper: Require tool to be enabled
   * Throws error if not enabled
   *
   * @throws Error if tool is not enabled
   *
   * @example
   * someMethod(): void {
   *   this.requireEnabled();
   *   // ... rest of method
   * }
   */
  protected requireEnabled(): void {
    if (!this._enabled) {
      throw new Error(`Tool "${this.name}" must be enabled to perform this action`);
    }
  }

  /**
   * Helper: Log debug message
   * Only logs if debug mode is enabled
   *
   * @param message - Debug message
   * @param args - Additional arguments
   *
   * @example
   * this.log('Measurement created', { x, y, z });
   */
  protected log(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[${this.name}] ${message}`, ...args);
    }
  }

  /**
   * Helper: Log warning message
   *
   * @param message - Warning message
   * @param args - Additional arguments
   */
  protected warn(message: string, ...args: unknown[]): void {
    console.warn(`[${this.name}] ${message}`, ...args);
  }

  /**
   * Helper: Log error message
   *
   * @param message - Error message
   * @param args - Additional arguments
   */
  protected error(message: string, ...args: unknown[]): void {
    console.error(`[${this.name}] ${message}`, ...args);
  }

  // ─── Undo/Redo ──────────────────────────────────────────────────────

  /**
   * Initialize undo/redo support for this tool.
   * Call this in the tool's constructor or onEnable().
   * Returns the manager so the tool can call pushState() / popState() directly.
   */
  protected initUndoRedo(): ToolUndoRedoManager {
    const mgr = new ToolUndoRedoManager();
    mgr.onStackChanged = () => this.dispatchUndoRedoChanged();
    this._undoRedo = mgr;
    return mgr;
  }

  /** Get the undo/redo manager (if initialized). Exposed for UI integration. */
  get undoRedo(): ToolUndoRedoManager | null {
    return this._undoRedo;
  }

  /**
   * Perform undo: pops the previous snapshot, which restores itself.
   */
  performUndo(): boolean {
    if (!this._undoRedo) return false;
    return this._undoRedo.undo();
  }

  /**
   * Perform redo: pops the next snapshot, which restores itself.
   */
  performRedo(): boolean {
    if (!this._undoRedo) return false;
    return this._undoRedo.redo();
  }

  /** Dispatch undo/redo availability change event for UI updates. */
  protected dispatchUndoRedoChanged(): void {
    if (!this._undoRedo) return;
    const event = {
      type: ToolEvents.UNDO_REDO_CHANGED,
      tool: this,
      canUndo: this._undoRedo.canUndo,
      canRedo: this._undoRedo.canRedo,
    };
    this.dispatchEvent(event);
    this.viewer.scene.dispatchEvent(event);
  }

  /**
   * Internal keyboard handler for undo/redo shortcuts.
   * Registered before the tool's onKeyDown so it can intercept Ctrl+Z / Ctrl+Y.
   */
  private handleUndoRedoKeyDown = (event: KeyboardEvent): void => {
    if (!this._undoRedo) return;

    // Ctrl+Z while typing in a field is the FIELD's text undo, not the
    // tool's scene undo.
    if (isTextEntryEventTarget(event.target)) return;

    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    if (!isCtrlOrMeta) return;

    let handled = false;

    // Ctrl+Z (undo) or Ctrl+Shift+Z (redo)
    if (event.key === 'z' || event.key === 'Z') {
      handled = event.shiftKey ? this.performRedo() : this.performUndo();
    }

    // Ctrl+Y (redo)
    if (event.key === 'y' || event.key === 'Y') {
      handled = this.performRedo();
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };
}
